import { createAsyncThunk } from "@reduxjs/toolkit"
import { AppThunkAPIType } from "../../app/appTypes"
import { deleteWithAuth, getWithAuth, postWithAuth, putWithAuth } from "../../http"

export interface OperationalDashboardOverviewSettings {
    showAgendaRating: boolean
    showTopicStatus: boolean
    showHeartbeat: boolean
    defaultAreaExpansion: "expanded" | "collapsed"
    linesInConsent: number
}

export const defaultOperationalDashboardOverviewSettings: OperationalDashboardOverviewSettings = {
    showAgendaRating: true,
    showTopicStatus: true,
    showHeartbeat: true,
    defaultAreaExpansion: "collapsed",
    linesInConsent: 2,
}

export type OperationalDashboardOverviewSettingsJson = Partial<{
    [Key in keyof OperationalDashboardOverviewSettings]: OperationalDashboardOverviewSettings[Key] | null
}>

export const getOperationalDashboardOverviewSettings = (
    configJson: OperationalDashboardOverviewSettingsJson | null | undefined,
): OperationalDashboardOverviewSettings => {
    const lines = configJson?.linesInConsent
    return {
        showAgendaRating: typeof configJson?.showAgendaRating === "boolean"
            ? configJson.showAgendaRating : defaultOperationalDashboardOverviewSettings.showAgendaRating,
        showTopicStatus: typeof configJson?.showTopicStatus === "boolean"
            ? configJson.showTopicStatus : defaultOperationalDashboardOverviewSettings.showTopicStatus,
        showHeartbeat: typeof configJson?.showHeartbeat === "boolean"
            ? configJson.showHeartbeat : defaultOperationalDashboardOverviewSettings.showHeartbeat,
        defaultAreaExpansion: configJson?.defaultAreaExpansion === "expanded" ? "expanded" : "collapsed",
        linesInConsent: typeof lines === "number" && Number.isSafeInteger(lines) && lines > 0
            ? lines : defaultOperationalDashboardOverviewSettings.linesInConsent,
    }
}

export interface OperationalDashboardDto {
    id: number
    guid: string
    companyId: number
    name: string
    description: string
    isActive: boolean
    ownerName: string
    isPublic: boolean
    configJson?: OperationalDashboardOverviewSettingsJson | null
}

export interface SaveOperationalDashboardAreaDto {
    id: number | null
    companyDepartmentId: number | null
    structureNodeId: number | null
    meetingAgendaItemSharedGuid: string | null
    name: string
    description: string
    order: number
    isActive: boolean
    responsibleUserName: string | null
    responsibleName: string
    responsibleEmail: string
}

export interface SaveOperationalDashboardDto {
    name: string
    description: string
    isActive: boolean
    ownerName: string
    isPublic: boolean
    configJson: OperationalDashboardOverviewSettings
    areas: SaveOperationalDashboardAreaDto[]
}

export interface SaveAreaTopicRequest {
    id: number | null
    name: string
    description: string
    displayOrder: number
}

export interface SaveAreaConfigurationRequest {
    name: string
    description: string
    companyDepartmentId: number | null
    structureNodeId: number | null
    responsibleUserName: string | null
    responsibleName: string | null
    responsibleEmail: string | null
    order: number
    isActive: boolean
    topics: SaveAreaTopicRequest[]
}

export interface UpdateOperationalDashboardAreaAgendaSummaryRequest {
    summary: string
}

export interface UpdateOperationalDashboardAreaContentTextRequest {
    text: string
}

export interface UpdateOperationalDashboardAreaContentPriorityRequest {
    priority: "L" | "M" | "H"
}

export interface UpdateOperationalDashboardAreaAgendaRatingRequest {
    rating: "GREEN" | "YELLOW" | "RED"
}

export interface UpdateOperationalDashboardAgendaDiscussionStatusRequest {
    meetingAgendaItemSharedGuid: string
    discussionStatus: string
}

export interface OperationalDashboardAreaConfigurationDto {
    id: number
    operationalDashboardId: number
    companyDepartmentId: number | null
    structureNodeId: number | null
    name: string
    description: string
    order: number
    isActive: boolean
    meetingAgendaItemSharedGuid: string | null
    meetingAgendaItemName: string | null
    responsibleUserName: string | null
    responsibleName: string
    responsibleEmail: string
}

export enum OperationalDashboardContentSourceTypeEnum {
    MM_ITEM = "MM_ITEM",
    STATIC_TEXT = "STATIC_TEXT",
}

export interface OperationalDashboardAreaContentConfigurationDto {
    id: number
    sourceType: OperationalDashboardContentSourceTypeEnum | 0 | 1
    sourceGuid: string | null
    name: string
    description: string
    displayOrder: number
    isVisible: boolean
}

export interface OperationalDashboardAreaConfigurationDetailsDto extends OperationalDashboardAreaConfigurationDto {
    contents: OperationalDashboardAreaContentConfigurationDto[]
}

export const isMeetingMinuteContent = (content: { sourceType: OperationalDashboardAreaContentConfigurationDto["sourceType"] }) =>
    content.sourceType === OperationalDashboardContentSourceTypeEnum.MM_ITEM || content.sourceType === 0

export const toSaveAreaConfigurationRequest = (configuration: OperationalDashboardAreaConfigurationDetailsDto): SaveAreaConfigurationRequest => ({
    name: configuration.name,
    description: configuration.description ?? "",
    companyDepartmentId: configuration.companyDepartmentId,
    structureNodeId: configuration.structureNodeId,
    responsibleUserName: configuration.responsibleUserName,
    responsibleName: configuration.responsibleName,
    responsibleEmail: configuration.responsibleEmail,
    order: configuration.order,
    isActive: configuration.isActive,
    topics: configuration.contents
        .filter(isMeetingMinuteContent)
        .sort((a, b) => a.displayOrder - b.displayOrder || a.id - b.id)
        .map(content => ({
            id: content.id,
            name: content.name,
            description: content.description,
            displayOrder: content.displayOrder,
        })),
})

export interface OperationalDashboardConfigurationDto {
    id: number
    meetingMinutesId: string | null
    name: string
    description: string
    isActive: boolean
    ownerName: string
    isPublic: boolean
    configJson?: OperationalDashboardOverviewSettingsJson | null
    createDate: string
    createdBy: string
    areas: OperationalDashboardAreaConfigurationDto[]
}

export interface DashboardTopicDto {
    id: number
    sharedGuid: string
    name: string
    itemType: string
    status: string | null
    priority: string | null
    responsiblePerson: string | null
    deadline: string | null
    tasks: DashboardTopicDto[]
}

export interface OperationalDashboardAreaContentOverviewDto {
    id: number
    sourceType: OperationalDashboardContentSourceTypeEnum | 0 | 1
    sourceGuid: string | null
    name: string
    description: string | null
    displayOrder: number
    item: DashboardTopicDto | null
}

export interface OperationalDashboardAreaOverviewDto {
    areaId: number
    name: string
    description: string 
    order: number
    responsibleName: string | null
    meetingAgendaItemId: number | null
    agendaName: string | null
    meetingResponsibleName: string | null
    discussionStatus: string | null
    rating: string | null
    summary: string | null
    decision: string | null
    departmentId: number | null
    departmentColor: string | null
    departmentCode: string | null
    items: DashboardTopicDto[]
    contents: OperationalDashboardAreaContentOverviewDto[]
}

export interface OperationalDashboardOverviewDto {
    dashboard: OperationalDashboardDto
    meetingMinutesId: string | null
    meetingMinutesStatus: string | null
    meetingId: number | null
    meetingName: string | null
    lastMeetingStartDate: string | null
    lastMeetingEndDate: string | null
    plannedMeetingTime: number | null
    areas: OperationalDashboardAreaOverviewDto[]
    otherItems: DashboardTopicDto[]
}

export interface OperationalDashboardMeetingItemHistoryDto {
    date: string
    user: string
    content: string
}

export const loadOperationalDashboards = createAsyncThunk<
    OperationalDashboardDto[],
    { includeInactive?: boolean } | void,
    AppThunkAPIType
>(
    "operationalDashboards/loadAll",
    async (params, { dispatch, rejectWithValue }) => {
        const includeInactive = params?.includeInactive ?? false
        const result = await dispatch(getWithAuth({
            url: `api/operational-dashboards?includeInactive=${includeInactive}`
        }))
        const { payload } = result

        if (getWithAuth.fulfilled.match(result)) {
            return payload as OperationalDashboardDto[]
        }
        return rejectWithValue(payload ?? { kind: "unknown" })
    }
)

export const loadOperationalDashboardOverview = createAsyncThunk<
    OperationalDashboardOverviewDto,
    { id: number; meetingId?: number | null },
    AppThunkAPIType
>(
    "operationalDashboards/loadOverview",
    async ({ id, meetingId }, { dispatch, rejectWithValue }) => {
        const url = `api/operational-dashboards/${id}/overview` +
            (meetingId == null ? "" : `?meetingId=${meetingId}`)
        const result = await dispatch(getWithAuth({ url }))
        const { payload } = result

        if (getWithAuth.fulfilled.match(result)) {
            return payload as OperationalDashboardOverviewDto
        }
        return rejectWithValue(payload ?? { kind: "unknown" })
    }
)

export const loadOperationalDashboardMeetingItemHistory = createAsyncThunk<
    OperationalDashboardMeetingItemHistoryDto[],
    { dashboardId: number; areaContentId: number },
    AppThunkAPIType
>(
    "operationalDashboards/loadMeetingItemHistory",
    async ({ dashboardId, areaContentId }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({
            url: `api/operational-dashboards/${dashboardId}/area-contents/${areaContentId}/meeting-items-history`
        }))
        const { payload } = result

        if (getWithAuth.fulfilled.match(result)) {
            return payload as OperationalDashboardMeetingItemHistoryDto[]
        }
        return rejectWithValue(payload ?? { kind: "unknown" })
    }
)

export const loadOperationalDashboardAreaMeetingAgendaItemHistory = createAsyncThunk<
    OperationalDashboardMeetingItemHistoryDto[],
    { dashboardId: number; areaId: number },
    AppThunkAPIType
>(
    "operationalDashboards/loadAreaMeetingAgendaItemHistory",
    async ({ dashboardId, areaId }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({
            url: `api/operational-dashboards/${dashboardId}/areas/${areaId}/meeting-agenda-items-history`
        }))
        const { payload } = result

        if (getWithAuth.fulfilled.match(result)) {
            return payload as OperationalDashboardMeetingItemHistoryDto[]
        }
        return rejectWithValue(payload ?? { kind: "unknown" })
    }
)

export const loadOperationalDashboardConfiguration = createAsyncThunk<
    OperationalDashboardConfigurationDto,
    number,
    AppThunkAPIType
>(
    "operationalDashboards/loadConfiguration",
    async (id, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({
            url: `api/operational-dashboards/${id}/configuration`
        }))
        const { payload } = result

        if (getWithAuth.fulfilled.match(result)) {
            return payload as OperationalDashboardConfigurationDto
        }
        return rejectWithValue(payload ?? { kind: "unknown" })
    }
)

export const loadOperationalDashboardAreaConfiguration = createAsyncThunk<
    OperationalDashboardAreaConfigurationDetailsDto,
    { dashboardId: number; areaId: number },
    AppThunkAPIType
>(
    "operationalDashboards/loadAreaConfiguration",
    async ({ dashboardId, areaId }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({
            url: `api/operational-dashboards/${dashboardId}/areas/${areaId}/configuration`
        }))
        const { payload } = result

        if (getWithAuth.fulfilled.match(result)) {
            return payload as OperationalDashboardAreaConfigurationDetailsDto
        }
        return rejectWithValue(payload ?? { kind: "unknown" })
    }
)

export const createOperationalDashboard = createAsyncThunk<
    OperationalDashboardDto,
    SaveOperationalDashboardDto,
    AppThunkAPIType
>(
    "operationalDashboards/create",
    async (dto, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({
            url: "api/operational-dashboards",
            payload: dto
        }))
        const { payload } = result

        if (postWithAuth.fulfilled.match(result)) {
            return payload as OperationalDashboardDto
        }
        return rejectWithValue(payload ?? { kind: "unknown" })
    }
)

export const updateOperationalDashboard = createAsyncThunk<
    OperationalDashboardDto,
    { id: number; dto: SaveOperationalDashboardDto },
    AppThunkAPIType
>(
    "operationalDashboards/update",
    async ({ id, dto }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({
            url: `api/operational-dashboards/${id}`,
            payload: dto
        }))
        const { payload } = result

        if (putWithAuth.fulfilled.match(result)) {
            return payload as OperationalDashboardDto
        }
        return rejectWithValue(payload ?? { kind: "unknown" })
    }
)

export const updateOperationalDashboardAreaConfiguration = createAsyncThunk<
    boolean,
    { id: number; areaId: number; dto: SaveAreaConfigurationRequest },
    AppThunkAPIType
>(
    "operationalDashboards/updateAreaConfiguration",
    async ({ id, areaId, dto }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({
            url: `api/operational-dashboards/${id}/areas/${areaId}/configuration`,
            payload: dto
        }))
        const { payload } = result

        if (putWithAuth.fulfilled.match(result)) {
            return payload as boolean
        }
        return rejectWithValue(payload ?? { kind: "unknown" })
    }
)

export const updateOperationalDashboardAreaContentText = createAsyncThunk<
    boolean,
    { dashboardId: number; areaContentId: number; dto: UpdateOperationalDashboardAreaContentTextRequest },
    AppThunkAPIType
>(
    "operationalDashboards/updateAreaContentText",
    async ({ dashboardId, areaContentId, dto }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({
            url: `api/operational-dashboards/${dashboardId}/area-contents/${areaContentId}/text`,
            payload: dto
        }))
        const { payload } = result

        if (putWithAuth.fulfilled.match(result)) {
            return payload as boolean
        }
        return rejectWithValue(payload ?? { kind: "unknown" })
    }
)

export const updateOperationalDashboardAreaContentPriority = createAsyncThunk<
    boolean,
    { dashboardId: number; areaContentId: number; dto: UpdateOperationalDashboardAreaContentPriorityRequest },
    AppThunkAPIType
>(
    "operationalDashboards/updateAreaContentPriority",
    async ({ dashboardId, areaContentId, dto }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({
            url: `api/operational-dashboards/${dashboardId}/area-contents/${areaContentId}/priority`,
            payload: dto
        }))
        const { payload } = result

        if (putWithAuth.fulfilled.match(result)) {
            return payload as boolean
        }
        return rejectWithValue(payload ?? { kind: "unknown" })
    }
)

export const updateOperationalDashboardAreaAgendaSummary = createAsyncThunk<
    boolean,
    { dashboardId: number; areaId: number; dto: UpdateOperationalDashboardAreaAgendaSummaryRequest },
    AppThunkAPIType
>(
    "operationalDashboards/updateAreaAgendaSummary",
    async ({ dashboardId, areaId, dto }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({
            url: `api/operational-dashboards/${dashboardId}/areas/${areaId}/agenda-summary`,
            payload: dto
        }))
        const { payload } = result

        if (putWithAuth.fulfilled.match(result)) {
            return payload as boolean
        }
        return rejectWithValue(payload ?? { kind: "unknown" })
    }
)

export const updateOperationalDashboardAreaAgendaRating = createAsyncThunk<
    boolean,
    { dashboardId: number; areaId: number; dto: UpdateOperationalDashboardAreaAgendaRatingRequest },
    AppThunkAPIType
>(
    "operationalDashboards/updateAreaAgendaRating",
    async ({ dashboardId, areaId, dto }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({
            url: `api/operational-dashboards/${dashboardId}/areas/${areaId}/agenda-rating`,
            payload: dto
        }))
        const { payload } = result

        if (putWithAuth.fulfilled.match(result)) {
            return payload as boolean
        }
        return rejectWithValue(payload ?? { kind: "unknown" })
    }
)

export const updateOperationalDashboardAgendaDiscussionStatus = createAsyncThunk<
    boolean,
    { dashboardId: number; dto: UpdateOperationalDashboardAgendaDiscussionStatusRequest },
    AppThunkAPIType
>(
    "operationalDashboards/updateAgendaDiscussionStatus",
    async ({ dashboardId, dto }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({
            url: `api/operational-dashboards/${dashboardId}/agenda-items/discussion-status`,
            payload: dto
        }))
        const { payload } = result

        if (putWithAuth.fulfilled.match(result)) {
            return payload as boolean
        }
        return rejectWithValue(payload ?? { kind: "unknown" })
    }
)

export const deactivateOperationalDashboard = createAsyncThunk<
    OperationalDashboardDto,
    number,
    AppThunkAPIType
>(
    "operationalDashboards/deactivate",
    async (id, { dispatch, rejectWithValue }) => {
        const result = await dispatch(deleteWithAuth({
            url: `api/operational-dashboards/${id}`
        }))
        const { payload } = result

        if (deleteWithAuth.fulfilled.match(result)) {
            return payload as OperationalDashboardDto
        }
        return rejectWithValue(payload ?? { kind: "unknown" })
    }
)